import type { MutableRefObject } from "react";

/** The send in progress: its address and the idempotency key that identifies it to the provider. */
export interface TestSendAttempt {
  to: string;
  key: string;
}

/**
 * How long an attempt is remembered: the email provider's own 24 hour idempotency window. A send
 * whose response was lost is ambiguous for that whole window, not merely while the request is in
 * flight: the provider still remembers the key, so a retry of the same address with a NEW key would
 * deliver a second message. An earlier one hour limit discarded the key while the provider still
 * held it (Codex on #6604). Past 24 hours the provider forgets the key too, so nothing is lost by
 * dropping it.
 */
export const TEST_SEND_ATTEMPT_TTL_MS = 24 * 60 * 60 * 1000;

const STORAGE_PREFIX = "forge:test-send-attempt:";

interface StoredAttempt extends TestSendAttempt {
  at: number;
}

const memory = new Map<string, StoredAttempt>();

function isStoredAttempt(value: unknown): value is StoredAttempt {
  if (typeof value !== "object" || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return typeof candidate.to === "string" && typeof candidate.key === "string" && typeof candidate.at === "number";
}

function loadFromStorage(scope: string): StoredAttempt | null {
  try {
    const raw = window.sessionStorage.getItem(STORAGE_PREFIX + scope);
    if (raw === null) return null;
    const parsed: unknown = JSON.parse(raw);
    return isStoredAttempt(parsed) ? parsed : null;
  } catch {
    // Storage unavailable (private mode, blocked) or the entry is not valid JSON.
    return null;
  }
}

function saveToStorage(scope: string, attempt: StoredAttempt | null): void {
  try {
    if (attempt === null) window.sessionStorage.removeItem(STORAGE_PREFIX + scope);
    else window.sessionStorage.setItem(STORAGE_PREFIX + scope, JSON.stringify(attempt));
  } catch {
    // The in-memory copy still covers navigation within this page load.
  }
}

/**
 * The current attempt for a scope (an organization and its sending domain), kept OUTSIDE any
 * component, as a ref-shaped accessor so TestEmailStep needs no change.
 *
 * A key held in component state or a ref is lost when the wizard page unmounts: the header's
 * "Back to Integrations", a sidebar link, the browser's back button. Those exits cannot all be
 * disabled while a request is pending, and returning to retry the same address would then mint a
 * new key and could deliver the test twice (Codex on #6586, round 7). This copy is held in memory,
 * so it survives the page unmounting and a route change, and in sessionStorage, so it also
 * survives a reload of the tab. It is not shared between tabs.
 *
 * It holds only the address the test was sent to and the key, and is dropped on success, when the
 * address changes, or after TEST_SEND_ATTEMPT_TTL_MS.
 *
 * An entry the previous release stored under `<organization>:<domain>` is deliberately NOT read.
 * It names no user, so any rule for deciding who made it is a guess, and the guesses failed in both
 * directions over four review rounds on #6606: adopting it hands one admin's attempt to another
 * after an account switch, and refusing it orphans the right admin's (decision on #6615). Such an
 * entry is inert, and clearAllTestSendAttempts removes it with the rest at sign-out. The cost is
 * that a send left ambiguous by the previous release, retried after this version loads, can deliver
 * one duplicate TEST email.
 *
 * A failed send does NOT put its key back when a sibling's success already cleared it (decision on
 * #6620). Two test steps can be sending for one domain at once when the page is left and reopened
 * mid-send, and a put-back drew a defect in three review rounds (a failure after sign-out, a fresh 24
 * hours for the key, a stale key restored over a newer send), each fix adding machinery. The cost: in
 * that stacked chain a retry can mint a new key, so at worst one duplicate TEST email.
 */
export function persistentTestSendAttempt(
  scope: string,
  now: () => number = Date.now,
): MutableRefObject<TestSendAttempt | null> {
  return {
    get current(): TestSendAttempt | null {
      const stored = memory.get(scope) ?? loadFromStorage(scope);
      if (stored === null) return null;
      if (now() - stored.at > TEST_SEND_ATTEMPT_TTL_MS) {
        memory.delete(scope);
        saveToStorage(scope, null);
        return null;
      }
      return { to: stored.to, key: stored.key };
    },
    set current(next: TestSendAttempt | null) {
      if (next === null) {
        memory.delete(scope);
        saveToStorage(scope, null);
        return;
      }
      const stored: StoredAttempt = { to: next.to, key: next.key, at: now() };
      memory.set(scope, stored);
      saveToStorage(scope, stored);
    },
  };
}

/**
 * Forget every attempt, in memory and in storage, for every account. Called when the user signs
 * out: an attempt holds the address a test was sent to and its key, and belongs to whoever made it.
 * (Attempts are also scoped by user id, so a later account never reads another's; this removes the
 * leftover rather than relying on scope alone.)
 */
export function clearAllTestSendAttempts(): void {
  memory.clear();
  try {
    const keys: string[] = [];
    for (let i = 0; i < window.sessionStorage.length; i += 1) {
      const key = window.sessionStorage.key(i);
      if (key !== null && key.startsWith(STORAGE_PREFIX)) keys.push(key);
    }
    for (const key of keys) window.sessionStorage.removeItem(key);
  } catch {
    // Storage unavailable: nothing was persisted there to remove.
  }
}

/** For tests: forget every in-memory attempt, as a fresh page load does. Storage is left alone. */
export function resetTestSendAttemptMemoryForTests(): void {
  memory.clear();
}
